import { Form, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Choice } from "../../requests/controls";
import { formatLocationTime } from "@/lib/location-time";

export type FacebookSetupProps = {
  available: boolean;
  environment: string;
  locations: { id: string; name: string }[];
  authorization: {
    id: string;
    location_name: string;
    pages: { id: string; name: string }[];
  } | null;
  pages: {
    id: string;
    name: string;
    location_id: string;
    ready: boolean;
    state: string;
    verified_at: string | null;
  }[];
};

export function FacebookSetup({ setup }: { setup: FacebookSetupProps }) {
  const { t } = useTranslation();
  const { i18n } = usePage().props;
  return (
    <Card>
      <CardHeader>
        <CardDescription>{t("facebook.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Badge variant="outline">
          {t(
            `facebook.environment.${setup.environment === "live" ? "live" : "test"}`,
          )}
        </Badge>
        <p className="text-muted-foreground text-sm">
          {t("facebook.constraints")}
        </p>
        <a
          className="inline-block text-sm underline underline-offset-4"
          href="https://developers.facebook.com/docs/messenger-platform/"
          target="_blank"
          rel="noreferrer"
        >
          {t("facebook.guide")}
        </a>
        {!setup.available && (
          <p className="text-muted-foreground text-sm">
            {t("facebook.unavailable")}
          </p>
        )}
        {setup.available && setup.locations.length > 0 && (
          <Form action="/user/facebook_authorization" method="post">
            {({ processing }) => (
              <div className="space-y-3">
                <Choice
                  id="facebook-intake-location"
                  name="facebook_authorization[location_id]"
                  label={t("facebook.default_location")}
                  options={setup.locations}
                  defaultValue={setup.locations[0].id}
                />
                <Button type="submit" disabled={processing}>
                  {t("facebook.authorize")}
                </Button>
              </div>
            )}
          </Form>
        )}
        {setup.available && setup.authorization && (
          <section
            className="space-y-3 rounded-md border p-3"
            aria-label={t("facebook.choose_page")}
          >
            <h3 className="font-medium">{t("facebook.choose_page")}</h3>
            <p className="text-muted-foreground text-sm">
              {t("facebook.intake_at", {
                location: setup.authorization.location_name,
              })}
            </p>
            {setup.authorization.pages.length === 0 ? (
              <p>{t("facebook.no_pages")}</p>
            ) : (
              <Form action="/user/facebook_pages" method="post">
                {({ processing }) => (
                  <div className="space-y-3">
                    <input
                      type="hidden"
                      name="facebook_page[authorization_id]"
                      value={setup.authorization!.id}
                    />
                    <Choice
                      id="facebook-page"
                      name="facebook_page[page_id]"
                      label={t("facebook.page")}
                      options={setup.authorization!.pages}
                      defaultValue={setup.authorization!.pages[0].id}
                    />
                    <Button type="submit" disabled={processing}>
                      {t("facebook.connect")}
                    </Button>
                  </div>
                )}
              </Form>
            )}
          </section>
        )}
        {setup.pages.map((page) => (
          <section
            className="space-y-3 rounded-md border p-3"
            key={page.id}
            aria-label={page.name}
          >
            <h3 className="font-medium break-words">{page.name}</h3>
            <p className="text-sm">
              {t(
                `facebook.states.${page.ready ? "ready" : page.state === "pending" ? "pending" : page.state === "disconnected" ? "disconnected" : "unavailable"}`,
              )}
            </p>
            {page.verified_at && (
              <p className="text-muted-foreground text-sm">
                {t("facebook.checked", {
                  date: formatLocationTime(page.verified_at, i18n, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }),
                })}
              </p>
            )}
            <Form action={`/user/facebook_pages/${page.id}`} method="patch">
              {({ processing }) => (
                <div className="space-y-3">
                  <Choice
                    id={`facebook-location-${page.id}`}
                    name="facebook_page[location_id]"
                    label={t("facebook.default_location")}
                    options={setup.locations}
                    defaultValue={page.location_id}
                  />
                  <Button type="submit" variant="outline" disabled={processing}>
                    {t("facebook.save_location")}
                  </Button>
                </div>
              )}
            </Form>
            {page.state !== "disconnected" && (
              <Form action={`/user/facebook_pages/${page.id}`} method="delete">
                {({ processing }) => (
                  <Button type="submit" variant="outline" disabled={processing}>
                    {t("facebook.disconnect")}
                  </Button>
                )}
              </Form>
            )}
          </section>
        ))}
      </CardContent>
    </Card>
  );
}
